import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  School, TrendingUp, Users, Trophy, Wallet, Globe,
  UserCheck, ClipboardList, CalendarClock, FileDown, Sparkles, MessageSquare,
  ChevronRight, RefreshCw, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { admAPI, downloadAuthedPdf } from "../../lib/admApi";
import { PageHeader, KPITile, Section, Loading, StatusPill, AIInsightCard, EmptyState } from "../../components/Common";

const STAGE_LABELS = {
  enquiry: "Enquiry",
  application_draft: "Draft",
  application_submitted: "Submitted",
  under_review: "Under review",
  assessment_scheduled: "Assessment",
  interview_scheduled: "Interview",
  review_complete: "Review complete",
  offer_pending: "Offer pending",
  offer_issued: "Offer issued",
  waiting_list: "Waiting list",
  accepted: "Accepted",
  enrolled: "Enrolled",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
};

export default function AdmissionsHub() {
  const { school } = useParams();
  const nav = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [genLoading, setGenLoading] = useState(false);

  const load = () => admAPI.kpis().then(({ data }) => setKpis(data)).catch(() => setKpis({}));

  useEffect(() => { load(); }, []);

  const generateExec = async () => {
    setGenLoading(true);
    try {
      const { data } = await admAPI.aiExecBriefing("Weekly");
      setBriefing(data);
      toast.success("Executive briefing generated (advisory)");
    } catch { toast.error("AI briefing failed"); }
    finally { setGenLoading(false); }
  };

  const download = async (url, name) => {
    try { await downloadAuthedPdf(url, name); }
    catch (e) { toast.error(e.message); }
  };

  const pipelineEntries = useMemo(() => {
    if (!kpis?.pipeline) return [];
    return Object.entries(kpis.pipeline).filter(([, v]) => v > 0);
  }, [kpis]);

  if (!kpis) return <Loading label="Loading admissions intelligence…" />;

  return (
    <div data-testid="admissions-hub">
      <PageHeader
        kicker="Admissions360™ · Executive Command Centre"
        title="Applicant lifecycle intelligence"
        subtitle="From enquiry to enrolment — international-benchmark decision support for the admissions team."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => nav(`/education360/${school}/admissions/pipeline`)}
              className="hl-button-secondary text-xs"
              data-testid="btn-open-pipeline"
            >
              Open pipeline
            </button>
            <button
              onClick={() => nav(`/education360/${school}/admissions/enquiries`)}
              className="hl-button-secondary text-xs"
              data-testid="btn-open-enquiries"
            >
              Enquiries
            </button>
            <button onClick={load} className="hl-button-secondary text-xs" title="Refresh">
              <RefreshCw className="w-3 h-3" />
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
        <KPITile testId="kpi-total" label="Applications" value={kpis.total} accent="Live" />
        <KPITile testId="kpi-offers" label="Offers issued" value={kpis.offers_issued} sub={`${kpis.yield_rate}% yield`} />
        <KPITile testId="kpi-accepted" label="Accepted" value={kpis.accepted} sub={`${kpis.conversion_rate}% conversion`} />
        <KPITile testId="kpi-enrolled" label="Enrolled" value={kpis.enrolled} tone="dark" />
        <KPITile testId="kpi-boarding" label="Boarding demand" value={kpis.boarding_demand} sub={`${kpis.waiting_list} on waiting list`} />
        <KPITile testId="kpi-revenue" label="Revenue forecast" value={`$${(kpis.revenue_forecast || 0).toLocaleString()}`} accent="Advisory" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Section title="Pipeline by stage" testId="pipeline-panel">
          <div className="hl-card p-5 space-y-2" data-testid="pipeline-list">
            {pipelineEntries.length === 0 && <div className="text-sm text-slate-500">No applications yet.</div>}
            {pipelineEntries.map(([stage, v]) => (
              <button
                key={stage}
                onClick={() => nav(`/education360/${school}/admissions/pipeline?stage=${stage}`)}
                className="w-full flex items-center justify-between text-left group py-1.5"
                data-testid={`pipeline-${stage}`}
              >
                <div className="flex items-center gap-3">
                  <StatusPill status={stage} />
                  <span className="text-sm text-slate-700">{STAGE_LABELS[stage] || stage}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-display font-bold text-lg text-slate-800">{v}</span>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[color:var(--hl-orange)]" />
                </div>
              </button>
            ))}
          </div>
          {kpis.bottlenecks_over_7_days > 0 && (
            <div className="hl-card p-4 mt-3 border-l-4 border-l-amber-500 flex items-start gap-3" data-testid="bottleneck-alert">
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
              <div className="text-sm text-slate-700">
                <b>{kpis.bottlenecks_over_7_days}</b> applicants have been idle in their current stage for more than 7 days.
                <button className="ml-2 text-[color:var(--hl-orange)] underline text-xs" onClick={() => nav(`/education360/${school}/admissions/pipeline`)}>
                  Review
                </button>
              </div>
            </div>
          )}
        </Section>

        <Section title="Demand by year group" testId="year-demand">
          <div className="hl-card p-5 space-y-2">
            {Object.entries(kpis.by_year || {}).sort().map(([yg, v]) => (
              <div key={yg} className="flex items-center gap-3 text-sm">
                <div className="w-20 text-slate-500">{yg}</div>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[color:var(--hl-navy)] rounded-full" style={{ width: `${Math.min(100, (v / kpis.total) * 100 * 3)}%` }} />
                </div>
                <div className="font-mono text-slate-700 w-8 text-right">{v}</div>
              </div>
            ))}
            {Object.keys(kpis.by_year || {}).length === 0 && (
              <div className="text-sm text-slate-500">No applications yet.</div>
            )}
          </div>
        </Section>

        <Section title="Countries of origin" testId="countries-panel">
          <div className="hl-card p-5 space-y-2">
            {Object.entries(kpis.by_country || {}).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([c, v]) => (
              <div key={c} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-slate-700">
                  <Globe className="w-3.5 h-3.5 text-slate-400" />{c}
                </div>
                <span className="font-mono text-slate-700">{v}</span>
              </div>
            ))}
            {Object.keys(kpis.by_country || {}).length === 0 && (
              <div className="text-sm text-slate-500">No data yet.</div>
            )}
          </div>
        </Section>
      </div>

      <Section
        title="Executive Intelligence"
        subtitle="AI-generated insights. Advisory only — final decisions rest with the admissions team."
        actions={
          <div className="flex gap-2">
            <button
              className="hl-button-secondary text-xs disabled:opacity-50"
              onClick={async () => {
                try {
                  const { data } = await admAPI.sendBriefingNow("Weekly");
                  if (data.success) toast.success(`Sent to ${data.recipients?.length || 0} recipient(s)`);
                  else toast.error(`Email queued (send failed: ${(data.error || "").slice(0, 80) || "unknown"})`);
                } catch { toast.error("Send failed"); }
              }}
              data-testid="btn-send-briefing-now"
            >
              <MessageSquare className="w-3 h-3 inline mr-1" />Send briefing now
            </button>
            <button
              className="hl-button-primary text-xs disabled:opacity-50"
              onClick={generateExec}
              disabled={genLoading}
              data-testid="btn-gen-exec-briefing"
            >
              <Sparkles className="w-3 h-3 inline mr-1" />
              {genLoading ? "Generating…" : "Generate briefing"}
            </button>
          </div>
        }
      >
        {briefing ? (
          <AIInsightCard testId="ai-exec-briefing" title={briefing.title} body={briefing.body} severity="advisory" />
        ) : (
          <EmptyState icon={Sparkles} title="No briefing yet" body="Click Generate briefing to draft a board-ready weekly summary." testId="no-briefing" />
        )}
      </Section>

      <Section title="Premium reports" subtitle="Server-generated PDFs, board-ready.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="reports-grid">
          <ReportCard label="Admissions Register" icon={ClipboardList} onClick={() => download(admAPI.registerPdf(), "admissions-register.pdf")} testId="dl-register" />
          <ReportCard label="Offer Register" icon={Trophy} onClick={() => download(admAPI.offerRegisterPdf(), "offer-register.pdf")} testId="dl-offer-register" />
          <ReportCard label="Interview Schedule" icon={CalendarClock} onClick={() => download(admAPI.interviewSchedulePdf(), "interview-schedule.pdf")} testId="dl-interviews" />
          <ReportCard label="Waiting List" icon={Users} onClick={() => download(admAPI.waitingListPdf(), "waiting-list.pdf")} testId="dl-waiting" />
          <ReportCard label="Executive Report" icon={TrendingUp} onClick={() => download(admAPI.executivePdf(), "executive-admissions.pdf")} testId="dl-exec" />
        </div>
      </Section>

      <Section title="Recent applicants" subtitle="Most recent applications">
        <div className="hl-card overflow-hidden" data-testid="recent-applicants">
          <table className="hl-table">
            <thead><tr>
              <th>App No.</th><th>Name</th><th>Year</th><th>Nationality</th><th>Priority</th><th>Stage</th><th></th>
            </tr></thead>
            <tbody>
              {(kpis.recent_applicants || []).map((a) => (
                <tr key={a.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/education360/${school}/admissions/applicant/${a.id}`)}>
                  <td className="mono">{a.application_no}</td>
                  <td>{a.first_name} {a.last_name}</td>
                  <td>{a.year_group_applied}</td>
                  <td>{a.nationality}</td>
                  <td className="capitalize">{a.priority}</td>
                  <td><StatusPill status={a.stage} /></td>
                  <td><ChevronRight className="w-4 h-4 text-slate-400" /></td>
                </tr>
              ))}
              {(kpis.recent_applicants || []).length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-slate-500">No applicants yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

function ReportCard({ label, icon: Icon, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="hl-card p-5 text-left flex items-start gap-3 hover:border-[color:var(--hl-orange)] transition-colors group"
    >
      <div className="w-10 h-10 rounded-md bg-[color:var(--hl-navy)] text-white grid place-items-center">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1">
        <div className="font-display font-bold text-slate-800">{label}</div>
        <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
          <FileDown className="w-3 h-3" /> Download PDF
        </div>
      </div>
    </button>
  );
}
